import { Button, Card, CardActions, CardContent, Grid, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import * as Const from "../constants/MandateConstants";
import FormTextField from "../../../experiments/forms/FormTextField";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import FormSelect from "../../../experiments/forms/FormSelect";
import { ISelectValues } from "../../cashback/constants/ISelectValues";
import { useNavigate, useParams } from "react-router-dom";
import { useSddRepository } from "../../../api/repositories/SddRepository";
import FormDatePicker from "../../../experiments/forms/FormDatePicker";
import dayjs from "dayjs";
import { useCBRepository } from "../../../api/repositories/CBRepository";
import FormAsyncAutocomplete from "../../../experiments/forms/FormAsyncAutocomplete";
import MandatesModal from "./MandatesModal";

import { ArrowBackRounded, EditRounded } from "@mui/icons-material";
import useToggle from "../hooks/useToggle";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";

function MandatesWizard(props: { type: string }) {
    const { t } = useTranslation("finance", { keyPrefix: "sdd.mandates" });
    const { dispatchGlobalData } = useContext(GlobalContext);
    const { getMandate, postMandate, putMandate} = useSddRepository()
    const { getCustomers } = useCBRepository()

    const navigate = useNavigate();
    const { id } = useParams();

    const [editable, setEditable] = useState<boolean>(props.type !== 'UPDATE');
    const [mandate, setMandate] = useState<any>();

    const [showUnsaved, toggleUnsaved] = useToggle();
    const [showDelete, toggleDelete] = useToggle();
    const [showRevoke, toggleRevoke] = useToggle();

    const schema = Yup.object().shape({
        //mandateStatus: Yup.string().required(),
        externalId: Yup.string().required(),
        type: Yup.string().required(),
        debtorName: Yup.string().test('invalid-debtorName', t("filters.errors.invalid-name"), function (value) {
            return !!value && value.length > 0;
        }),
        debtorSapId: Yup.string().test('invalid-sapId', t("filters.errors.invalid-sapid"), function (value) {
            return !!value && value.length > 0;
        }),
        debtorTaxOrVatCode: Yup.string().required(),
        debtorIban: Yup.string().required(),
        debtorBank: Yup.string().nullable(),
        debtorCountry: Yup.string().nullable(),
        debtorRegion: Yup.string().nullable(),
        debtorProvince: Yup.string().nullable(),
        debtorCity: Yup.string().nullable(),
        debtorAddress: Yup.string().nullable(),
        debtorPostalCode: Yup.string().nullable(),
        subscriberFirstName: Yup.string().nullable(),
        subscriberLastName: Yup.string().nullable(),
        subscriberTaxCode: Yup.string().nullable(),
        subscriptionDate: Yup.date().required().typeError(t('pages.wizard.form.error-subscription-date')),
        notes: Yup.string().max(255).nullable()
    }); 

    const defaultValues = {
        externalId: "",
        type: "",
        //mandateStatus: "",
        debtorName: "",
        debtorSapId: "",
        debtorTaxOrVatCode: "",
        debtorIban: "",
        debtorBank: "",
        debtorCountry: "",
        debtorRegion: "",
        debtorProvince: "",
        debtorCity: "",
        debtorAddress: "",
        debtorPostalCode: "",
        subscriberFirstName: "",
        subscriberLastName: "",
        subscriberTaxCode: "",
        subscriptionDate: null,
        notes: "",
    };

    const { control, setValue, watch, handleSubmit, getValues, formState, trigger} = useForm<any>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        //check for changes in the form
        let dirty = formState.isDirty;
    }, [formState])

    const watcher = watch();

    const onSubmit: SubmitHandler<any> = (data) => {

        delete data['debtor'];
        delete data['mandateStatus'];
        if (props.type === 'CREATE') {
            postMandate(data).then((response) => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.success-create'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            }).catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.error-create'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            });

        } else if (props.type === 'UPDATE') {
            data['id'] = id;
            putMandate(data).then((response) => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.success-update'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            }).catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.error-update'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            });
        }
        navigate(`/finance/payments/sdd/mandates`)
    }




    function checkCancel() {
        if (formState.isDirty) {
            toggleUnsaved();
        } else {
            navigate(-1);
        }
    }



    useEffect(() => {
        if (props.type === 'UPDATE' && id !== undefined) {

            getMandate(id).then((response) => {
                setMandate(response.result);
                let data = response.result;
                setValue('type', data.type);
                setValue('status', data.status);
                setValue('externalId', data.externalId);
                setValue('debtorSapId', data.debtorSapId);
                setValue('debtorName', data.debtorName);
                setValue('debtorIban', data.debtorIban);
                setValue('debtorBank', data.debtorBank);
                setValue('debtorTaxOrVatCode', data.debtorTaxOrVatCode);
                setValue('debtorCountry', data.debtorCountry);
                setValue('debtorRegion', data.debtorRegion);
                setValue('debtorProvince', data.debtorProvince);
                setValue('debtorCity', data.debtorCity);
                setValue('debtorAddress', data.debtorAddress);
                setValue('debtorPostalCode', data.debtorPostalCode);
                setValue('subscriberFirstName', data.subscriberFirstName);
                setValue('subscriberLastName', data.subscriberLastName);
                setValue('subscriberTaxCode', data.subscriberTaxCode);
                setValue('subscriptionDate', data.subscriptionDate);
                setValue('notes', data.notes);
            })
        }
    }, [id])

    function cancel() {
        navigate(-1)
    }

    return (
        <Card variant="outlined" sx={{ margin: '16px' }}>
            {showUnsaved &&
                <MandatesModal
                    open={showUnsaved}
                    title={t("modal.unsavedChanges")}
                    body={t("modal.deleteBodyMessage")}
                    leftButton={t("modal.actionButtons.cancel")}
                    leftFunction={cancel}
                    rightButton={t("modal.actionButtons.confirm")}
                    rigthFunction={toggleUnsaved}
                    onClose={toggleUnsaved}

                />
            }
            {showRevoke &&
                <MandatesModal
                    open={showRevoke}
                    viewNote={true}
                    form={{ id: mandate.id, status: 'REVOKED' }}
                    title={t("modal.revoke.unsavedChanges")}
                    body={t("modal.revoke.deleteBodyMessage", { user: mandate.debtorName })}
                    leftButton={t("modal.revoke.actionButtons.cancel")}
                    leftFunction={toggleRevoke}
                    rightButton={t("modal.revoke.actionButtons.confirm")}
                    rigthFunction={() => { }}
                    onClose={toggleRevoke}
                />
            }
            {showDelete &&
                <MandatesModal
                    open={showDelete}
                    viewNote={true}
                    form={{ id: mandate.id, status: 'CANCELLED' }}
                    title={t("modal.delete.unsavedChanges")}
                    body={t("modal.delete.deleteBodyMessage", { user: mandate.debtorName })}
                    leftButton={t("modal.delete.actionButtons.cancel")}
                    leftFunction={toggleDelete}
                    rightButton={t("modal.delete.actionButtons.confirm")}
                    rigthFunction={() => { }}
                    onClose={toggleDelete}
                />
            }

            <form onSubmit={handleSubmit(onSubmit)}>
                <CardContent>
                    <Grid container direction={'row'} rowGap={5}>
                        {showCardHeader()}
                        <Grid container item spacing={2} alignItems={'center'}>
                            <Grid item xs={12}>
                                <Typography variant={'body2'} color={'primary.light'} fontWeight={700} textTransform={'uppercase'}>
                                    {t('pages.wizard.form.title-all')}
                                </Typography>
                            </Grid>
                            {props.type === 'UPDATE' &&
                                <Grid item xs={12}>
                                    <TextField
                                        name={`status`}
                                        value={getValues('status')}
                                        disabled={true}
                                    />
                                </Grid>
                            }
                            <Grid item md={6} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'externalId'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-mandate-external-id'),
                                        sx: { width: '100%' }
                                    }}

                                />
                            </Grid>
                            <Grid item md={6} sm={6} xs={12}>
                                <FormSelect
                                    name={`type`}
                                    control={control}
                                    props={{
                                        disabled: !editable,
                                        variant: "standard", fullWidth: true, label: t('pages.wizard.form.label-mandate-type'),

                                    }}
                                    items={Const.TYPES.map((item: ISelectValues, index: number) => (
                                        <MenuItem key={index} value={item.value}>
                                            {t(item.label)}
                                        </MenuItem>
                                    ))}
                                />
                            </Grid>
                        </Grid>
                        <Grid container item spacing={2} alignItems={'center'}>
                            <Grid item xs={12}>
                                <Typography variant={'body2'} color={'primary.light'} fontWeight={700} textTransform={'uppercase'}>
                                    {t('pages.wizard.form.title-debtor')}
                                </Typography>
                            </Grid>
                            <Grid container item spacing={2} alignItems={'center'}>
                                <Grid item md={6} sm={12} xs={12}>
                                    <FormAsyncAutocomplete
                                        name={'debtor'}
                                        control={control}
                                        props={{
                                            callback: (it) => getCustomers(it),
                                            onSelect: (option) => {
                                                setValue('debtorName', option?.value?.name)
                                                trigger('debtorName')
                                                setValue('debtorSapId', option?.value?.sapId)
                                                trigger('debtorSapId')
                                                setValue('debtorTaxOrVatCode', option?.value?.taxCodeOrVatNumber)
                                                trigger('debtorTaxOrVatCode')
                                            },
                                            disabled: !editable,
                                            getOptionLabel: (option) => option?.value?.name,
                                            renderInput: (params) =>
                                                <TextField
                                                    {...params}
                                                    label={t('pages.wizard.form.label-debtor-search-name')}
                                                />
                                        }}
                                    />
                                </Grid>

                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorName'}
                                    props={{
                                        disabled: true,
                                        label: t('pages.wizard.form.label-debtor-name'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorSapId'}
                                    props={{
                                        disabled: true,
                                        label: t('pages.wizard.form.label-debtor-sap-id'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorTaxOrVatCode'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-debtor-tax-or-vat-code'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={6} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorIban'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-debtor-iban'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={6} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorBank'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-debtor-bank-name'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorCountry'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-debtor-country'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorRegion'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-debtor-region'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorCity'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-debtor-city'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorProvince'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-debtor-province'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={9} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorAddress'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-debtor-address'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'debtorPostalCode'}
                                    matcher={/^\d+$/}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-debtor-postal-code'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container item spacing={2} alignItems={'center'}>
                            <Grid item xs={12}>
                                <Typography variant={'body2'} color={'primary.light'} fontWeight={700} textTransform={'uppercase'}>
                                    {t('pages.wizard.form.title-subscriber')}
                                </Typography>
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'subscriberFirstName'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-subscriber-first-name'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'subscriberLastName'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-subscriber-last-name'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'subscriberTaxCode'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-subscriber-tax-code'),
                                        sx: { width: '100%' },
                                    }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormDatePicker
                                    control={control}
                                    name={'subscriptionDate'}
                                    props={{
                                        disabled: !editable,
                                        label: t('pages.wizard.form.label-subscription-date'),
                                        maxDate: dayjs().toDate(),
                                    }}
                                />
                            </Grid>
                        </Grid>
                        {props.type === 'UPDATE' &&
                            <Grid container item spacing={2} alignItems={'center'}>
                                <Grid item xs={12}>
                                    <Typography variant={'body2'} color={'primary.light'} fontWeight={700} textTransform={'uppercase'}>
                                        {t('pages.wizard.form.title-notes')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormTextField name={'notes'} control={control}
                                        props={{
                                            disabled: !editable,
                                            label: t('pages.wizard.form.label-notes'),
                                            sx: { width: '100%' },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                    {showCardActions()}
                </CardActions>
            </form>
        </Card>

    );

    function showCardActions() {
        let status = mandate?.status?.toUpperCase();
        if (props.type === 'CREATE') {
            return (
                <>
                    <Button variant={"outlined"} color={"accent"} onClick={() => navigate(-1)}>{t('pages.wizard.buttons.cancel')}</Button>
                    <Button color={"accent"} type="submit">{t('pages.wizard.buttons.create')}</Button>
                </>
            );
        }
        if (props.type === 'UPDATE') {
            if (editable) {
                return (
                    <>
                        <Button variant={"outlined"} color={"accent"} onClick={() => checkCancel()}>{t('pages.wizard.buttons.cancel')}</Button>
                        <Button color={"accent"} type="submit">{t('pages.wizard.buttons.save')}</Button>
                    </>
                );
            } else if (status === Const.STATUS_READY) {
                return (
                    <Button color={"accent"} onClick={toggleDelete}>
                        {t('pages.wizard.buttons.delete')}
                    </Button>
                );
            } else if (status === Const.STATUS_ACTIVE) {
                return (
                    <Button color={"accent"} onClick={toggleRevoke}>
                        {t('pages.wizard.buttons.revoked')}
                    </Button>
                );
            }



        }
    }


    function showCardHeader() {
        let status = mandate?.status.toUpperCase();
        if (props.type === 'UPDATE') {
            return (
                <Grid container item>
                    <Grid item xs={6}>
                        <Stack direction={'row'} alignItems={'center'}>
                            <IconButton color={'primary'} onClick={() => checkCancel()}>
                                <ArrowBackRounded />
                            </IconButton>
                            <Typography variant={'body2'} color={'primary'} fontWeight={700} textTransform={'uppercase'}>
                                {watcher.debtorName}
                            </Typography>
                        </Stack>
                    </Grid>
                    {status === 'READY' ? (
                        <Grid item xs={6} display={'flex'} justifyContent={'flex-end'}>
                            <Button disabled={editable} variant={"text"} color={"accent"} startIcon={<EditRounded />} onClick={() => {
                                setEditable(true)
                            }}>{t('pages.wizard.buttons.update')}</Button>
                        </Grid>
                    ) : null}
                </Grid>

            );
        }
    }
}

export default MandatesWizard;